<script>
    import {showSignupModal} from '@js/actions/userActions';
    import {addRide, updateRide, fetchRideTracks} from '@js/actions/rideActions';
    import {showProductModal} from '@js/actions/basketActions';
    import {addPlanPoi, updatePlanPoi} from '@js/actions/poiActions';

    import {userStore} from '@js/stores/userStore';
    import {mapStore} from '@js/stores/mapStore';
    import {poiStore} from '@js/stores/poiStore';

    import * as Utils from '@js/modules/utils';
    import I18n from '@js/modules/translations';
    import Computation from '@js/modules/computation';

    import {buildPoiFormData, buildRideFormData} from '@js/modules/ride/formHelper';

    import Notification from '@js/components/layouts/notification';

    import TimePicker from '@js/components/themes/TimePicker.svelte';

    import BuildMap from '@js/components/maps/BuildMap.svelte';

    import PlanFormName from '@js/components/plan/form/PlanFormName.svelte';
    import PlanFormDescription from '@js/components/plan/form/PlanFormDescription.svelte';
    import PlanFormDate from '@js/components/plan/form/PlanFormDate.svelte';

    import RideFormType from '@js/components/ride/form/RideFormType.svelte';
    import RideFormLevel from '@js/components/ride/form/RideFormLevel.svelte';
    import RideEstimateDuration from '@js/components/ride/shared/RideEstimateDuration.svelte';

    import {userNewRidePath, userEditRidePath} from '@js/constants/routesHelper';

    const {
        planPoiCoordinates,
        planPoi,
        planRide,
        onModalClose
    } = $props();

    let currentTime = $state(planRide?.chronoDuration || '00:00:00');

    let isPlanningRide = $state(!!planRide);
    let planRideTracks = $state(null);
    // let isSubmitting = $state(false);
    let rideErrors = $state();

    if (planRide) {
        fetchRideTracks(planRide?.id)
            .then((rideData) => {
                if (!rideData) {
                    return;
                }

                planRideTracks = rideData.tracks;
            });

        mapStore.currentDistance = planRide.distance;
        mapStore.currentDuration = planRide.duration;
    }

    const _handlePlanRide = (event) => {
        event.preventDefault();

        isPlanningRide = !isPlanningRide;
    };

    const _handleDistanceChange = (distance) => {
        mapStore.currentDistance = distance;
    };

    const _handleTimeChange = (duration, chronoDuration) => {
        currentTime = chronoDuration;

        mapStore.currentDuration = duration;
    };

    const _handleDurationEstimated = (estimatedChronoTime) => {
        currentTime = estimatedChronoTime;
    };

    const _saveRide = (formData) => {
        return (planRide ? updateRide(planRide.id, formData) : addRide(formData))
            .then((response) => {
                if (response.errors) {
                    if (response.errors.ride_max_count_reached) {
                        showProductModal('ride_max_count');
                        return false;
                    }

                    const errors = {};

                    if (response.errors.distance) {
                        errors.distance = I18n.t('js.ride.errors.distance', {
                            min: Computation.formatDistance(window.settings.ride_min_distance, {
                                unit: $userStore.unit,
                                smartUnit: true,
                                kUnit: false
                            }),
                            max: Computation.formatDistance(window.settings.ride_max_distance, {
                                unit: $userStore.unit,
                                smartUnit: true,
                                kUnit: true
                            })
                        });
                        Utils.scrollTo('ride-distance', 150);
                    } else if (response.errors.duration) {
                        errors.duration = I18n.t('js.ride.errors.duration', {
                            min: Computation.formatDuration(window.settings.ride_min_duration),
                            max: Computation.formatDuration(window.settings.ride_max_duration, {withSeconds: false})
                        });
                        Utils.scrollTo('ride-duration', 150);
                    } else if (response.errors.name && response.errors.name[0] === 'is reserved') {
                        errors.name = I18n.t('js.ride.errors.name_reserved');
                    } else if (response.errors.name) {
                        errors.name = I18n.t('js.ride.errors.name', {
                            min: window.settings.ride_name_min_length,
                            max: window.settings.ride_name_max_length
                        });
                    }

                    rideErrors = errors;

                    return errors;
                } else {
                    return response;
                }
            });
    };

    const _savePoi = (poiData) => {
        return (
            planPoi
                ?
                updatePlanPoi(planPoi.id, poiData, true)
                :
                addPlanPoi(poiData)
        )
            .then((response) => {
                if (!poiData) {
                    Notification.hide();
                }
                // isSubmitting = false;

                if (response.errors) {
                    Notification.hide();

                    if (response.errors.plan_poi_max_count_reached) {
                        showProductModal('plan_poi_max_count');
                        return false;
                    }
                } else {
                    setTimeout(() => Notification.hide(), 2000);

                    poiStore.planPois = poiStore.planPois.replaceOrAdd('id', response.poi);
                    poiStore.map.fetchPlanPOIs(poiStore.selectedFilters);

                    onModalClose();

                    return response;
                }
            });
    };

    const _savePlanPoi = (form, poiData) => {
        if (isPlanningRide) {
            const rideData = buildRideFormData(
                form,
                {
                    name: poiData.get('poi[title]'),
                    rideType: poiData.get('poi[place_type]'),
                    plannedPoiId: planPoi?.id,
                    visibility: 'only_me',
                    planned: true,
                    plannedAt: poiData.get('poi[planned_at]'),
                    distance: mapStore.currentDistance,
                    duration: mapStore.currentDuration,
                    durationComputed: false,
                    speed: null,
                    speedComputed: true,
                    isMapEdited: true,
                    points: mapStore.map?.serializePoints(),
                    staticMap: mapStore.map?.staticMapUrl()
                }
            );

            if (rideData.rideErrors) {
                // isSubmitting = false;

                return false;
            }

            Notification.info(I18n.t('js.ride.plan.modal.adding_ride'));

            _saveRide(rideData)
                .then((rideResponse) => {
                    if (!rideResponse?.ride) {
                        return;
                    }

                    poiData.append('poi[ride_id]', rideResponse.ride.id);
                    poiData.append('poi[ride_user_id]', rideResponse.ride.userId);

                    _savePoi(poiData);
                });
        } else {
            Notification.info(I18n.t('js.ride.plan.modal.adding_poi'));

            _savePoi(poiData);
        }
    };

    const _handleSubmit = (event) => {
        event.preventDefault();

        // isSubmitting = true;

        const form = event.target;
        if (!form.checkValidity()) {
            return false;
        }

        const poiData = buildPoiFormData(
            form,
            {
                plan: true,
                poiCoordinates: planPoiCoordinates
            }
        );

        if (poiData.poiErrors) {
            // isSubmitting = false;

            return false;
        }

        if (!userStore.isConnected) {
            Notification.info(I18n.t('js.ride.map.create_account'));

            userStore.noRedirection = true;

            userStore.subscribe((userState) => {
                if (userState.isConnected && poiData) {
                    _savePlanPoi(form, poiData);
                }
            });

            showSignupModal();
        } else {
            _savePlanPoi(form, poiData);
        }
    };
</script>

<form id="plan-form"
      accept-charset="UTF-8"
      onsubmit={_handleSubmit}>
    <div class="flex items-center justify-between p-4 md:p-5 border-b rounded-t dark:border-gray-600">
        <h3 class="text-xl font-bold text-gray-900">
            {I18n.t('js.maps.poi.plan.modal.name')}
        </h3>

        <button type="button"
                class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 ms-auto inline-flex justify-center items-center dark:hover:bg-gray-600 dark:hover:text-white"
                onclick={onModalClose}>
            <svg class="w-4 h-4"
                 aria-hidden="true"
                 xmlns="http://www.w3.org/2000/svg"
                 fill="none"
                 viewBox="0 0 14 14">
                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"
                      stroke-width="2"
                      d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
            </svg>
            <span class="sr-only">Close modal</span>
        </button>
    </div>

    <div class="my-4 md:p-5 space-y-4">
        <div class="mx-auto w-4/5">
            <div class="flex items-center justify-center flex-col gap-6">
                <div class="grid gap-8 w-full mx-auto md:grid-cols-2 md:w-4/5">
                    <PlanFormName name="poi[title]"
                                  defaultValue={planPoi?.title}/>

                    <RideFormType name="poi[place_type]"
                                  label={I18n.t('js.ride.plan.model.place_type')}
                                  defaultValue={planPoi?.rideType}/>
                </div>

                <PlanFormDescription name="poi[description]"
                                     defaultValue={planPoi?.description}/>

                <PlanFormDate nameDateType="poi[plan_date_type]"
                              defaultDateType={planPoi?.planDateType}
                              namePlannedAt="poi[planned_at]"
                              defaultPlannedAt={planPoi?.plannedAt}
                              namePerformedAt="ride[performed_at]"
                              defaultPerformedAt={planRide?.performedAt}/>
            </div>

            {#if !isPlanningRide}
                <div class="flex items-center justify-center mt-8">
                    <button type="button"
                            class="button-primary-outlined"
                            onclick={_handlePlanRide}>
                        {I18n.t('js.ride.plan.modal.associate')}
                    </button>
                </div>
            {/if}

            {#if !isPlanningRide && planPoi?.id}
                <div class="flex items-center justify-center mt-5">
                    <a class="button-primary-link"
                       href={userNewRidePath(window.currentUserSlug) + `?plan_poi_id=${planPoi.id}`}>
                        {I18n.t('js.ride.plan.modal.page_associate')}
                    </a>
                </div>
            {/if}

            {#if planRide}
                <div class="mx-auto my-6 text-center">
                    <h3 class="mb-3 text-base font-bold">
                        {I18n.t('js.ride.plan.modal.complete.title')}
                    </h3>

                    <div>
                        <a class="button-primary-outlined"
                           href={userEditRidePath(planRide.userId, planRide.id)}>
                            {I18n.t('js.ride.plan.modal.complete.button')}
                        </a>
                    </div>
                </div>
            {/if}
        </div>
    </div>

    {#if isPlanningRide}
        <div class="mx-2">
            {#if !planRide || (planRide && planRideTracks)}
                <BuildMap mapId="plan-ride-map"
                          reusePreviousTracks={false}
                          initialMarkerPosition={planRide ? null : [planPoiCoordinates.lat, planPoiCoordinates.lng]}
                          initialMapPoints={$state.snapshot(planRideTracks)}
                          onDistanceChange={_handleDistanceChange}/>
            {/if}
        </div>

        <div class="grid gap-8 mt-6 mb-10 w-full mx-auto md:grid-cols-2 md:w-4/5 p-4">
            <div>
                <h4 class="text-base font-bold">
                    {I18n.t('js.ride.plan.modal.time')}
                </h4>

                <TimePicker currentTime={currentTime}
                            onTimeChange={_handleTimeChange}/>

                <div class="my-4">
                    <RideEstimateDuration displayHelper={false}
                                          onDurationEstimated={_handleDurationEstimated}/>
                </div>
            </div>

            <div>
                <h4 class="mb-4 text-base font-bold">
                    {I18n.t('js.ride.plan.modal.properties')}
                </h4>

                <RideFormLevel name="ride[level]"
                               defaultValue={planRide?.level}/>
            </div>
        </div>

        {#if !planRide}
            <div class="flex items-center justify-center my-4">
                <button type="button"
                        class="button-primary-outlined"
                        onclick={_handlePlanRide}>
                    {I18n.t('js.ride.plan.modal.cancel_map')}
                </button>
            </div>
        {/if}
    {/if}

    {#if planRide && rideErrors}
        <ul id="ride-errors"
            class="mx-auto my-4 text-center text-red-600 text-sm max-w-md space-y-1 list-disc list-inside">
            {#each Object.entries(rideErrors) as [errorName, errorValue] (errorName)}
                <li>
                    {errorValue}
                </li>
            {/each}
        </ul>
    {/if}

    <div class="flex items-center justify-around w-full p-4 md:p-5 space-x-3 border-t border-gray-200 rounded-b">
        <button type="button"
                class="py-2.5 px-5 ms-3 text-sm font-medium text-gray-900 focus:outline-hidden bg-white rounded-lg border border-gray-200 hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-4 focus:ring-gray-100 dark:focus:ring-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600 dark:hover:text-white dark:hover:bg-gray-700"
                onclick={onModalClose}>
            {I18n.t('js.ride.plan.modal.cancel')}
        </button>

        <button type="submit"
                class="button-primary-contained">
            {I18n.t(`js.ride.plan.modal.${!!planPoi ? 'update' : 'add'}`)}
        </button>
    </div>
</form>

<style>
</style>
